import React from 'react';
import { Helmet } from 'react-helmet';
import SimpleHero from '../../components/SimpleHero/SimpleHero';
import FeatureList from '../../components/Minimal/FeatureList';
import bank from '../../assets/images/patriot.png';
import './AccountAgreement.css';

const AccountAgreement = () => {
  // Get the current URL for sharing
  const currentUrl = typeof window !== 'undefined' 
    ? window.location.origin + window.location.pathname
    : 'https://gnbank.net/AccountAgreement';
  
  // OG image URL for social sharing
  const ogImageUrl = 'https://gnbank.net/og.png';

  return (
    <div>
      <Helmet>
        <title>Deposit Account Agreement and Disclosures | GN Bank</title>
        <meta 
          name="description" 
          content="Review GN Bank's Consolidated Deposit Account Agreement and Disclosures. Learn about account terms, fees, funds availability, and your rights and responsibilities as a customer." 
        />
        
        {/* Open Graph / Facebook / LinkedIn */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content="Account Agreement and Disclosures | GN Bank" />
        <meta 
          property="og:description" 
          content="Review GN Bank's Account Agreement and Disclosures. Learn about account terms, fees, and your rights and responsibilities." 
        />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="GN Bank" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content="Deposit Account Agreement and Disclosures | GN Bank" />
        <meta 
          name="twitter:description" 
          content="Review GN Bank's Consolidated Deposit Account Agreement and Disclosures." 
        />
        <meta name="twitter:image" content={ogImageUrl} />
        
        {/* Additional meta tags */}
        <meta name="keywords" content="account agreement, deposit agreement, banking terms, account fees, GN Bank, banking disclosures" />
        <link rel="canonical" href={currentUrl} />
      </Helmet>
      <SimpleHero
        title="Deposit Account Agreement"
        subtitle="Consolidated Deposit Account Agreement and Disclosures for personal and business accounts at GN Bank."
        variant="sofi"
        imageSrc={bank}
        imageAlt="Account Agreement"
        imageOn="right"
        tiltImage
        badges={["Account Terms", "Fees & Disclosures", "Customer Rights"]}
      />
      <FeatureList
        items={[
          { title: 'Account Terms', desc: 'Comprehensive information about account types and requirements.' },
          { title: 'Fee Disclosure', desc: 'Transparent details about all account fees and charges.' },
          { title: 'Your Rights', desc: 'Understanding your rights and responsibilities as a customer.' },
        ]}
      />
      <section className="section">
        <div className="gn-container">
          <div className="account-agreement-content">
            <div className="agreement-header">
              <h1>GN Bank – Consolidated Deposit Account Agreement and Disclosures</h1>
              <p className="effective-date">Effective January 15, 2025 (Revised September 1, 2025)</p>
            </div>

            <p className="agreement-intro">
              GN Bank is a covered savings association regulated by the Office of the Comptroller of the Currency (OCC) and Member FDIC. This Agreement governs personal and business deposit accounts at GN Bank. It replaces prior versions and incorporates by reference the current Fee Disclosure posted at <a href="/fees-disclosure">gnbank.net/fees-disclosure</a> (the "Schedule of Fees").
            </p>

            <div className="agreement-section">
              <h2>1. Your Contract with Us</h2>
              <p>When you open or keep an account, you agree to this Agreement, the Schedule of Fees, and any account-opening documents you sign. We may change this Agreement and the Schedule of Fees as permitted by law; continued use of your account means you accept the changes. We provide notice as the law allows (e.g., statement message, letter, or electronic notice).</p>
            </div>

            <div className="agreement-section">
              <h2>2. Governing Law; Oversight</h2>
              <p>This Agreement is governed by federal law and, to the extent not preempted, the laws of the State of Illinois, without regard to conflict-of-laws principles. GN Bank is regulated by the Office of the Comptroller of the Currency (OCC). Deposits are insured by the FDIC up to applicable limits.</p>
            </div>

            <div className="agreement-section">
              <h2>3. Accounts We Offer</h2>
              <ul>
                <li><strong>Personal Checking</strong> (non-interest bearing unless otherwise disclosed), minimum to open: $100; monthly maintenance fee: $0 (see Schedule of Fees for any special account features/waivers).</li>
                <li><strong>Statement Savings</strong> (may be interest-bearing as disclosed in our rate sheet), minimum to open: $250.00; monthly maintenance fee: $15.</li>
                <li><strong>Money Market Deposit Account (MMDA)</strong> (may be interest-bearing), minimum to open: $2,500; monthly maintenance fee: $15.</li>
                <li><strong>Small Business Checking</strong>, minimum to open: $100; monthly maintenance fee: $25.</li>
              </ul>
              <p>We may add or discontinue accounts at any time. If an account type is discontinued, we may convert your account to the most similar available account after notice.</p>
            </div>

            <div className="agreement-section">
              <h2>4. Identification and Privacy</h2>
              <p>We obtain and verify information under the USA PATRIOT Act (e.g., name, address, date of birth, TIN) and may request documentary and non-documentary verification. Our Consumer Privacy Notice explains how we collect, use, and share personal information; see <a href="/privacy">gnbank.net/privacy</a>.</p>
            </div>

            <div className="agreement-section">
              <h2>5. Account Ownership</h2>
              <p>We rely on our records to determine ownership. For joint accounts, all owners are co‑owners with the right of survivorship unless prohibited by law, and each may act alone. For custodial (UTMA/UGMA) and fiduciary accounts, you agree to comply with applicable law. Business accounts must be established and used for business purposes only.</p>
            </div>

            <div className="agreement-section">
              <h2>6. Using and Accessing Your Account</h2>
              <p>You can bank with us at our financial center(s), through Online/Mobile Banking, and by telephone banking. The availability of services may vary or be interrupted for maintenance or security reasons.</p>
            </div>

            <div className="agreement-section">
              <h2>7. Deposits; Our Right to Reject/Reverse</h2>
              <p>We may accept, for collection only, refuse, or return all or part of any deposit. We may adjust your account to correct errors. If an item you deposit is returned, we may debit your account and any interest credited on that item, even if this overdraws your account. We may reverse credits that were made in error or based on incorrect information.</p>
            </div>

            <div className="agreement-section">
              <h2>8. Posting and Processing Order</h2>
              <p>We receive transactions throughout the day and generally determine posting order during nightly processing. We may post credits, holds, and debits in any order permitted by law. The order in which transactions are posted may increase the number of overdrafts or returned items and related fees.</p>
            </div>

            <div className="agreement-section">
              <h2>9. Funds Availability (Regulation CC Summary)</h2>
              <ul>
                <li>Our general policy is to make cash and check deposits available no later than the first business day after the day of deposit. Electronic direct deposits and incoming wires are available the day we receive them.</li>
                <li>Longer delays may apply (e.g., new accounts, large deposits, repeatedly overdrawn accounts, suspicious or uncollectible checks, emergencies). We will notify you and tell you when funds will be available. The first $275 of your check deposits will generally be available by the first business day after the day of deposit.</li>
                <li>ATM and remote deposits may be subject to extended availability. See your statement and Online/Mobile Banking for current status of deposits.</li>
                <li>If GN Bank for any reason chooses to implement a case-by-case hold, at least, the first $275 of the held deposit will be made available by the next business day.</li>
                <li>GN Bank may delay funds availability "for a reasonable period of time" because some transactions present a higher level of risk to the financial institution.</li>
                <li>Reasonable period of time is typically defined as one additional business day for on-us checks (for a total of two business days) and five additional business days for other checks (for a total of seven business days).</li>
                <li><strong>New Accounts:</strong> An account is considered new if each individual on the account has not had a transaction at the institution within the previous 30 days, for a period of at least 30 days. For new customers, only the first $6,725 qualifies for the next day availability. The amount over $6,725 will be made available by the ninth business day after the day of deposit.</li>
                <li><strong>Large Deposits:</strong> Under the large deposit exception, only the amount in excess of $6,725 will be held until the seventh business day.</li>
                <li><strong>Redeposited Checks:</strong> Checks of this nature pose a greater risk to the bank because it can be returned the second time. Full amount will be held until the seventh business day. However, the exception will not apply to a check that was re-deposited because it was returned due to a missing endorsement or postdated.</li>
                <li><strong>Reasonable Cause to Doubt Collectability:</strong> If GN Bank reasonably believes it will not receive payment for a check (because the check is more than six months old or because the paying institution has sent a stop payment notice), it will place a hold on the full amount of the deposited item until the seventh business day.</li>
                <li><strong>Emergency Conditions:</strong> Although rarely used, checks that are deposited during situations outside of GN Bank's control, like a power failure or natural disaster, will be held for any length of time, with justification and notice sent to customers.</li>
                <li>In accordance with Regulation CC (12 CFR Part 229) and the bank's internal policies: Any single check exceeding $6,725 is considered a large deposit and may be subject to an extended hold. The bank reserves the right to aggregate multiple checks deposited at the same time. If the total deposit exceeds $6,725, even when individual checks are below the threshold, the deposit may be placed on hold. Customers will be notified whenever a hold is placed on any deposit, whether due to a single large check or aggregated deposit. Notification includes the reason for the hold and the expected availability date.</li>
              </ul>
            </div>

            <div className="agreement-section">
              <h2>10. Withdrawals and Paying Items</h2>
              <p>We may pay or return items presented against your account and are not required to examine every item. We may refuse to cash a check payable to you. Stale‑dated (over six months) and postdated checks may be paid unless you place a stop payment.</p>
            </div>

            <div className="agreement-section">
              <h2>11. Overdrafts and Returned Items</h2>
              <p>If you do not have enough available funds to cover an item, we may either pay it and overdraw your account (an overdraft item) or return it unpaid (a returned item), at our discretion.</p>
              <ul>
                <li><strong>Overdraft/Returned Item Fees:</strong> See Section 17 (Fees) and the Schedule of Fees for amounts and when they are charged.</li>
                <li>We generally do not charge an overdraft fee on everyday non‑recurring debit card transactions that were authorized when your available balance was sufficient and later settled when insufficient. We may decline one-time debit card or ATM transactions when funds are insufficient.</li>
                <li>You agree to promptly repay us for any overdraft, fees, and other amounts you owe.</li>
              </ul>
            </div>

            <div className="agreement-section">
              <h2>12. Stop Payments</h2>
              <p>You may request a stop payment on an unpaid check or ACH item. We require sufficient information (e.g., account number, check number, exact amount, and payee) and a reasonable time to act. Stop payment orders generally expire after 12 months unless renewed. Fees apply.</p>
            </div>

            <div className="agreement-section">
              <h2>13. Statements and Notices</h2>
              <p>We provide a periodic statement for checking, savings, and MMDA accounts when there is activity. Promptly review statements and notify us of problems or unauthorized transactions. For most issues you must notify us within 60 days after we make the statement available.</p>
            </div>

            <div className="agreement-section">
              <h2>14. Electronic Banking (Regulation E – Personal Accounts)</h2>
              <p>Tell us AT ONCE if your card, PIN, or Online/Mobile Banking credentials are lost or stolen, or if you believe an EFT has been made without your permission. If you tell us within 2 business days, your loss is limited to $50; otherwise up to $500, and more if you wait longer than 60 days after we send your statement. Call <a href="tel:7736242000">773‑624‑2000</a> or write to GN Bank, 4619 S King Drive, Chicago, IL 60653. We investigate errors and, when required, provide provisional credit within 2 business days (longer timeframes may apply in limited cases). We do not charge a fee to investigate a reported EFT error where prohibited by law.</p>
            </div>

            <div className="agreement-section">
              <h2>15. Wires and ACH (UCC Article 4A/NACHA)</h2>
              <p>Funds transfer you send or receive may be processed via Fedwire or ACH and are subject to applicable system rules and UCC 4A. We currently offer domestic wires only. We may rely on account numbers even if they identify a different name. Fees apply (see Section 17).</p>
            </div>

            <div className="agreement-section">
              <h2>16. Our Rights—Setoff; Security Interest; Legal Process; Freezes</h2>
              <p>We may set off or debit your accounts for any amounts you owe us, except where prohibited by law (e.g., tax‑advantaged retirement accounts). We may comply with legal process (e.g., levies, garnishments, subpoenas) and charge a fee. We may freeze or delay transactions if we suspect fraud, irregular activity, or to comply with law.</p>
            </div>

            <div className="agreement-section">
              <h2>17. Fees</h2>
              <p className="section-note">See the Schedule of Fees available on GN Bank's Portal (Copilot) for full, current details; selected items summarized below.</p>
              
              <h3>Account Fees</h3>
              <ul>
                <li><strong>Personal Checking:</strong> Minimum to open $100; Monthly maintenance fee $0.</li>
                <li><strong>Statement Savings:</strong> Minimum to open $250.00; Monthly maintenance fee $15.</li>
                <li><strong>Money Market Deposit Account (MMDA):</strong> Minimum to open $2,500; Monthly maintenance fee $15.</li>
                <li><strong>Small Business Checking:</strong> Minimum to open $100; Monthly maintenance fee $25.</li>
              </ul>

              <h3>Overdraft & Related</h3>
              <ul>
                <li><strong>Non‑Sufficient Funds</strong> (when we pay) — $35.00 per transaction.</li>
                <li><strong>Returned Item</strong> (when we return unpaid) — $35.00 per transaction.</li>
                <li><strong>Extended Overdrawn Account Fee</strong> — $35.00 charged daily for each day the account remains overdrawn.</li>
                <li><strong>Savings Excess Transaction Fee</strong> — $3.00 per transfer when the account exceeds six transfers/withdrawals in a statement period.</li>
              </ul>

              <h3>ATM/Debit</h3>
              <ul>
                <li><strong>Non‑GN Bank ATM usage</strong> ("Foreign ATM") — $3.00 per transaction.</li>
                <li><strong>ATM cash withdrawal fee</strong> at GN Bank ATMs for non‑GN Bank customers — $3.00 per transaction (if applicable at GN Bank‑owned ATMs used by non‑customers).</li>
              </ul>

              <h3>Wires/Transfers</h3>
              <ul>
                <li><strong>Outgoing domestic wire</strong> — $30.00 per transfer.</li>
                <li><strong>Incoming wire</strong> — $15.00 per transfer.</li>
                <li><strong>Customer direct transfer</strong> — $10.00 per transfer.</li>
              </ul>

              <h3>Documents/Research/Other</h3>
              <ul>
                <li><strong>Stop payment</strong> (check, ACH, or electronic payment) — $35.00 per request.</li>
                <li><strong>Account closed within 90 days</strong> of opening — $15.00.</li>
                <li><strong>Dormant account</strong> — $15.00 per month.</li>
                <li><strong>Check printing</strong> — varies by style.</li>
                <li><strong>Duplicate account statement</strong> — $8.00 per statement.</li>
                <li><strong>Official bank checks</strong> — First check each month $0.00; each additional check $1.00.</li>
                <li><strong>Rolled coin purchase</strong> — $0.10 per roll.</li>
                <li><strong>Non‑customer check cashing</strong> — GN Bank does not cash checks for non‑customers.</li>
              </ul>

              <p>We may add or change fees as permitted by law. Operators of non‑GN Bank ATMs and networks may also charge you.</p>
            </div>

            <div className="agreement-section">
              <h2>18. Statements, Errors, and Your Duties</h2>
              <p>Promptly review statements and report issues. Except as otherwise provided by law (e.g., for EFTs and substitute checks), you must notify us in writing within 60 days after we make the statement available or you may not recover for later items by the same wrongdoer.</p>
            </div>

            <div className="agreement-section">
              <h2>19. Unclaimed Property; Dormancy</h2>
              <p>Accounts may be classified as dormant/inactive under state law and charged a dormancy fee. We escheat abandoned property to the state as required.</p>
              <p className="note"><strong>Note:</strong> Account status is changed to dormant when inactive consecutively for six months.</p>
            </div>

            <div className="agreement-section">
              <h2>20. Changes, Closing, and Conversion</h2>
              <p>You or we may close an account at any time. We may convert an account to a different type if it no longer meets usage or eligibility criteria, after notice. We may close accounts for misuse, fraud, or threatening/abusive conduct toward employees or customers.</p>
            </div>

            <div className="agreement-section">
              <h2>21. Dispute Resolution; Venue</h2>
              <ul>
                <li><strong>Personal Accounts:</strong> Any claim will be resolved in court by a judge without a jury to the extent permitted by law; you and we waive jury trial.</li>
                <li><strong>Business Accounts:</strong> Either party may elect binding arbitration before AAA or JAMS under their rules. No class arbitration or class claims. Venue for court actions and for arbitration will be Cook County, Illinois.</li>
              </ul>
            </div>

            <div className="agreement-section">
              <h2>22. FDIC Insurance and Pass‑Through Coverage</h2>
              <p>Deposits are insured by the FDIC up to at least $250,000 per depositor, per ownership category. Accounts established by agents or fiduciaries may be eligible for pass‑through insurance when recordkeeping requirements are met.</p>
            </div>

            <div className="agreement-section">
              <h2>23. Contact Us</h2>
              <div className="contact-info">
                <p><strong>GN Bank</strong></p>
                <p>4619 S King Drive, Chicago, IL 60653</p>
                <p>Phone: <a href="tel:7736242000">773‑624‑2000</a></p>
              </div>
            </div>

            <div className="agreement-footer">
              <p>This Agreement supersedes prior GN Bank deposit agreements as of the revision date above.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccountAgreement;
